import { useCallback, useEffect, useRef, useState } from 'react';
import { UpsGroupingAPI } from '@src/api/returns';
import {
  setReturnShipments,
  setUpsGrouping,
  useReturnStore,
} from '@src/stores/returnStore';
import { adaptReturnMethodsResponse } from '@src/utils/eligibility-utils';
import {
  applyGroupingToShipments,
  buildUpsGroupingRequest,
  ChosenBoxCounts,
  describeGroupingMismatch,
} from '@src/utils/upsGroupingUtil';
import { ItemToReturn } from '@src/models/returns';
import { emitReturnsBusinessError } from '@src/utils/returns-instrumentation-context';

/**
 * What the call means for the step that asked.
 *
 * `superseded` is not a failure and must not be shown as one: the attempt no
 * longer owns the step, so it has nothing to report and nothing to advance.
 * Collapsing it into `failed` tells a customer who walked away that something
 * broke, and writes state to a step that is no longer mounted.
 */
export type UpsGroupingOutcome = 'ok' | 'failed' | 'superseded';

/**
 * Reason codes for the reasons a request was never sent. Spelled out rather
 * than interpolated from the status: these are the strings dashboards key on,
 * and the rest of this call's codes are snake_case.
 */
const NO_REQUEST_ERROR_CODES = {
  destinationMissing: 'ups_grouping_destination_missing',
  originMissing: 'ups_grouping_origin_missing',
} as const;

/**
 * Asks the shipping service which drop-off lines ship together and how many
 * boxes each group needs, once the customer has chosen a return method and
 * before anything is created.
 *
 * The call reserves nothing and persists nothing, so repeating it is safe and a
 * failed attempt leaves nothing behind to clean up beyond the grouping itself.
 */
export const useUpsGrouping = () => {
  const [isGroupingLoading, setIsGroupingLoading] = useState(false);
  // Go Back stays enabled while this is in flight, so the customer can leave
  // the step with an answer still on its way. Two different jobs, both needed:
  // isInFlight refuses to start a second request, and currentAttempt decides
  // whether an answer already coming back is still the one being waited for.
  // The first cannot cover unmount and the second cannot stop a duplicate
  // request. Same pair, for the same reason, as the return methods call.
  //
  // Being unmounted does not make a late write harmless: these land on the
  // store, not on this hook's state, so a superseded attempt would rewrite the
  // shipments of a step the customer has already left.
  const isInFlight = useRef(false);
  const currentAttempt = useRef(0);

  /**
   * Disowns whatever is in flight. Unmounting is not the only way the customer
   * leaves: going back from the per-item screen to the whole-order one changes
   * the step without unmounting, so the cleanup below never runs and an answer
   * still on its way would write shipments and advance the flow, undoing the
   * back they just pressed. Any transition away from the step has to say so.
   */
  const abandonGrouping = useCallback(() => {
    currentAttempt.current += 1;
  }, []);

  useEffect(() => abandonGrouping, [abandonGrouping]);

  /**
   * Resolves `ok` when the flow may continue — including when there was
   * nothing to ask about — `failed` when the customer must be shown an error
   * and try again, and `superseded` when this attempt no longer owns the step.
   */
  const fetchUpsGrouping = useCallback(
    async (
      modifiedItems: ItemToReturn[],
      // The counts the customer had already entered, for the paths that re-ask
      // after they have chosen. Empty on the way to the box step, where there is
      // nothing chosen yet to preserve.
      chosenCounts: ChosenBoxCounts = {},
    ): Promise<UpsGroupingOutcome> => {
      // An explicit guard rather than relying on the button being disabled. The
      // disabled button is presentation; this is what actually stops a second
      // request, and it holds however the call is reached — including from the
      // Keep It fallback.
      // Not a failure: the request already running owns the step, and it is the
      // one whose answer will be acted on.
      if (isInFlight.current) return 'superseded';

      // Read now rather than from a render closure: the methods answer is applied
      // to the store during the same press that leads here, so a value captured
      // when this component last rendered can predate it.
      const { orderDetailsV2, returnMethodsResponse } =
        useReturnStore.getState();
      // Neither should be missing by the time a method has been chosen, so this
      // holds the step rather than waving it through. Continuing would mean
      // showing the grouping the validator supplied and a picker with no floor —
      // the customer could then choose fewer boxes than the service will accept,
      // which is silently overridden at submit and is the outcome this call
      // exists to prevent.
      if (!orderDetailsV2 || !returnMethodsResponse) {
        emitReturnsBusinessError('ups_grouping_prerequisites_missing', {
          source: 'ups_grouping',
        });
        setUpsGrouping(undefined);
        return 'failed';
      }

      const adaptedMethods = adaptReturnMethodsResponse(returnMethodsResponse);
      const outcome = buildUpsGroupingRequest({
        orderDetailsV2,
        selectedItems: modifiedItems,
        adaptedMethods,
        deliveryDictionary: orderDetailsV2.deliveryDictionary,
      });

      // No parcel leg, so nothing to group. Any grouping still held belongs to an
      // earlier selection and is cleared rather than left to describe this one.
      if (outcome.status === 'noParcelLeg') {
        setUpsGrouping(undefined);
        return 'ok';
      }

      // A line with nowhere to go, or nowhere to go from, cannot be grouped, and
      // continuing anyway is the one outcome this call exists to prevent: the
      // picker would open with no floor and the customer could choose fewer boxes
      // than the service accepts. Deliberately not folded in with the case above —
      // all three mean "no request", and only one of them is harmless.
      if (outcome.status !== 'ready') {
        emitReturnsBusinessError(NO_REQUEST_ERROR_CODES[outcome.status], {
          source: 'ups_grouping',
        });
        setUpsGrouping(undefined);
        return 'failed';
      }

      currentAttempt.current += 1;
      const attempt = currentAttempt.current;
      isInFlight.current = true;
      setIsGroupingLoading(true);
      try {
        const { data } = await UpsGroupingAPI(outcome.request);
        // Checked before anything is read or written, so a superseded attempt
        // neither rewrites the shipments nor advances the step it no longer
        // belongs to. Everything below this point owns the step.
        if (currentAttempt.current !== attempt) return 'superseded';
        // A root-level error is the service telling us the answer is unusable,
        // which arrives on a 2xx as readily as on a failure status.
        if (data?.errors?.length || !data?.rlcReturns?.length) {
          emitReturnsBusinessError(
            data?.errors?.[0]?.code || 'ups_grouping_empty',
            {
              source: 'ups_grouping',
            },
          );
          setUpsGrouping(undefined);
          return 'failed';
        }

        // Non-empty is not the same as complete. The legs built below replace the
        // drop-off legs wholesale, so a line the answer leaves out is dropped
        // from the return and one it names twice is submitted twice — neither of
        // which the customer would see before the return is created.
        const mismatch = describeGroupingMismatch(
          data.rlcReturns,
          modifiedItems,
        );
        if (mismatch) {
          emitReturnsBusinessError(`ups_grouping_${mismatch}`, {
            source: 'ups_grouping',
          });
          setUpsGrouping(undefined);
          return 'failed';
        }

        setUpsGrouping(data.rlcReturns);
        // The service's grouping replaces the one the validator supplied, so the
        // review screen shows what will actually ship together rather than a
        // second opinion formed here. Read now rather than from the closure: the
        // validator wrote the legs during this same press.
        setReturnShipments(
          applyGroupingToShipments(
            data.rlcReturns,
            useReturnStore.getState().returnShipments,
            adaptedMethods,
            chosenCounts,
          ),
        );
        return 'ok';
      } catch {
        // Same ownership check the success path makes, and needed for the same
        // reason: the clear below writes to the store, so unmounting does not
        // make it harmless — a rejection belonging to an abandoned attempt could
        // otherwise wipe a grouping a later, successful one had already stored.
        if (currentAttempt.current !== attempt) return 'superseded';
        // On the critical path for every parcel return, so an outage here has to
        // be visible rather than only showing up as customers not completing.
        emitReturnsBusinessError('ups_grouping_failed', {
          source: 'ups_grouping',
        });
        setUpsGrouping(undefined);
        return 'failed';
      } finally {
        isInFlight.current = false;
        // Only the live attempt owns the spinner: a superseded one clearing it
        // would stop the spinner for the request still running.
        if (currentAttempt.current === attempt) setIsGroupingLoading(false);
      }
      // Everything this touches is a ref, a setter or read through getState(),
      // so nothing from a render is captured and the identity can hold. It has
      // to: the review-screen edit path keeps this in a dependency list, and an
      // identity that changed every render would rebuild that chain with it.
    },
    [],
  );

  return { fetchUpsGrouping, abandonGrouping, isGroupingLoading };
};

export default useUpsGrouping;
